import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-16 md:pt-18 flex items-center justify-center">
          <div className="text-center">
            <i className="ri-article-line text-5xl text-foreground-300 mb-4 block"></i>
            <p className="text-foreground-500 mb-4">文章未找到</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const otherArticles = articles.filter((a) => a.id !== article.id).slice(0, 3);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-16 md:pt-18">
        <article className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '文章' },
              { label: article.title },
            ]}
          />

          <div className="py-8 md:py-12">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700 whitespace-nowrap">
                  {article.category}
                </span>
                <span className="text-xs text-foreground-500">{article.publishedAt}</span>
                <span className="text-xs text-foreground-400">·</span>
                <span className="text-xs text-foreground-500">{article.readTime}阅读</span>
              </div>

              <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>

              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                {article.excerpt}
              </p>
            </div>

            <div className="rounded-2xl overflow-hidden mb-10">
              <img
                src={article.coverImage}
                alt={article.title}
                title={`${article.title} - 嫩小兔`}
                className="w-full aspect-[2/1] object-cover"
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
                [&_h4]:font-heading [&_h4]:text-xl [&_h4]:text-foreground-900 [&_h4]:mt-8 [&_h4]:mb-4
                [&_p]:mb-4 [&_p]:text-foreground-700
                [&_strong]:text-foreground-900
                [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-4
                [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-4
                [&_li]:mb-1 [&_li]:text-foreground-700"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-8 pt-6 border-t border-background-200/70">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 rounded-full text-xs bg-background-100 text-foreground-600 whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {otherArticles.length > 0 && (
            <section className="pb-14 md:pb-20">
              <h3 className="font-heading text-xl md:text-2xl text-foreground-950 mb-6">
                更多文章
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                {otherArticles.map((a) => (
                  <Link
                    key={a.id}
                    to={`/article/${a.slug}`}
                    className="group bg-background-50 rounded-2xl border border-background-200/70 overflow-hidden hover:border-primary-200/60 transition-all duration-300 cursor-pointer"
                  >
                    <div className="aspect-[16/10] overflow-hidden bg-background-100">
                      <img
                        src={a.coverImage}
                        alt={a.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 text-xs text-foreground-500 mb-2">
                        <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs whitespace-nowrap">
                          {a.category}
                        </span>
                        <span>{a.readTime}阅读</span>
                      </div>
                      <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2">
                        {a.title}
                      </h4>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            image: article.coverImage,
            datePublished: article.publishedAt,
            author: {
              '@type': 'Organization',
              name: '嫩小兔',
            },
            publisher: {
              '@type': 'Organization',
              name: '嫩小兔',
              url: 'https://www.nenxiaotu.com/',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'https://www.nenxiaotu.com/',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: '文章',
                },
                {
                  '@type': 'ListItem',
                  position: 3,
                  name: article.title,
                },
              ],
            },
          }),
        }}
      />
    </>
  );
}